import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import {
  fetchUsersByIds,
  sendEventInvitation,
} from "../services/meetlyService";
import { getCurrentUserId } from "../services/userService";

/**
 * Custom hook to manage event attendees data, live counts, and invitations.
 */
export function useEvent(event, userData, userInterests, isJoined) {
  const [showAttendees, setShowAttendees] = useState(false);
  const [showInviteScreen, setShowInviteScreen] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [realAttendees, setRealAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [liveAttendeeCount, setLiveAttendeeCount] = useState(0);
  const [selectedAttendeeProfile, setSelectedAttendeeProfile] = useState(null);

  // Reset drawer states when switching events
  // (this hook is reused across events without unmounting, so leftover
  // "showAttendees"/"showInviteScreen" state from a previous event would
  // otherwise carry over when the user opens a different one)
  useEffect(() => {
    setShowAttendees(false);
    setShowInviteScreen(false);
    setSelectedAttendeeProfile(null);
  }, [event?.id]);

  // Fetch live attendee count
  // Reads the event's attendee id list directly from Firestore and adds 1
  // locally if the current user just joined but that write hasn't been
  // reflected in the fetched document yet.
  useEffect(() => {
    async function fetchLiveCount() {
      try {
        const eventRef = doc(db, "events", event.id);
        const eventSnap = await getDoc(eventRef);

        const ids = eventSnap.exists() ? eventSnap.data().attendees || [] : [];
        const myUid = getCurrentUserId(userData);
        const totalCount =
          isJoined && myUid && !ids.includes(myUid)
            ? ids.length + 1
            : ids.length;

        setLiveAttendeeCount(totalCount);
      } catch (error) {
        console.error("Error fetching live attendee count:", error);
      }
    }
    fetchLiveCount();
  }, [event.id, isJoined, userData]);

  // Fetch detailed list of attendees when drawer is toggled
  // Only runs when the attendees drawer is actually opened (not on every
  // event view), to avoid the cost of resolving every attendee's profile
  // up front. If the current user is joined, their own profile is added
  // first from local userData (no extra fetch needed for themselves).
  useEffect(() => {
    async function fetchDetailedAttendees() {
      if (!showAttendees) return;

      setLoadingAttendees(true);
      try {
        const fetchedUsers = [];
        const myUid = getCurrentUserId(userData);

        if (isJoined && myUid) {
          fetchedUsers.push({
            id: myUid,
            name: userData?.name || auth.currentUser?.displayName || "You",
            avatar: userData?.avatar || "Mateo",
            avatarStyle:
              userData?.avatarStyle || userData?.style || "open-peeps",
            interests: userData?.interests || userInterests,
            bio: userData?.bio || "Connecting at events across Dublin.",
            joinedCommunities: userData?.joinedCommunities || [],
          });
        }

        const eventRef = doc(db, "events", event.id);
        const eventSnap = await getDoc(eventRef);

        if (eventSnap.exists()) {
          const attendeeIds = (eventSnap.data().attendees || []).filter(
            (userId) => userId !== myUid
          );

          // Single batched query instead of one getDoc() per attendee.
          const usersMap = await fetchUsersByIds(attendeeIds);
          attendeeIds.forEach((userId) => {
            const userDoc = usersMap.get(userId);
            if (userDoc) {
              fetchedUsers.push({ id: userId, ...userDoc });
            }
          });
        }

        setRealAttendees(fetchedUsers);
      } catch (error) {
        console.error("Error fetching detailed attendees:", error);
      } finally {
        setLoadingAttendees(false);
      }
    }
    fetchDetailedAttendees();
  }, [showAttendees, isJoined, event.id, userData, userInterests]);

  // Handle sending an event invitation to Firestore
  // Guards against sending without a session and against double-inviting
  // the same person, surfacing both as a toast rather than failing silently.
  const handleSendInvitation = async (targetUser, e) => {
    e?.stopPropagation?.();
    const targetId = String(targetUser.id || targetUser.uid);
    const myUid = getCurrentUserId(userData);

    if (!myUid) {
      setToastMessage("Error: User session not found.");
      setTimeout(() => setToastMessage(null), 3500);
      return;
    }

    if (invitedUsers.includes(targetId)) {
      setToastMessage(
        `Invitation already sent to ${targetUser.name || "User"}.`
      );
      setTimeout(() => setToastMessage(null), 3500);
      return;
    }

    try {
      await sendEventInvitation(
        { ...userData, name: userData?.name || auth.currentUser?.displayName },
        myUid,
        targetUser,
        event
      );

      setInvitedUsers([...invitedUsers, targetId]);
      setToastMessage(
        `Invitation sent to ${targetUser.name || "User"} for ${event.title}!`
      );
    } catch (error) {
      console.error("Error sending invitation document:", error);
      setToastMessage("Failed to send invitation. Check Firestore rules.");
    } finally {
      setTimeout(() => setToastMessage(null), 3500);
    }
  };

  return {
    showAttendees,
    setShowAttendees,
    showInviteScreen,
    setShowInviteScreen,
    invitedUsers,
    toastMessage,
    realAttendees,
    loadingAttendees,
    liveAttendeeCount,
    selectedAttendeeProfile,
    setSelectedAttendeeProfile,
    handleSendInvitation,
  };
}
